import { UserPermissionsContext } from '@client/Context/UserPermissions';
import { AppEvent, EventBusInstance, LogLevel, Sisp, showBanner } from '@sprint/sprint-react-components';
import {
    addMinutes,
    format,
    getHours,
    getMinutes,
    isPast,
    isSameDay,
    parse,
    roundToNearestMinutes,
    setHours,
    setMinutes,
    startOfMinute,
} from 'date-fns';
import React, { FormEvent, FunctionComponent, useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import ClientTagPill from '../../../../CommonComponents/ClientTags/ClientTagPill';
import TagsEditDropdown from '../../../../CommonComponents/ClientTags/TagsEditDropdown';
import HelpTooltip from '../../../../CommonComponents/Tooltips/HelpTooltip';
import { CampaignsRequest } from '../../Api/CampaignsRequest';
import CampaignType from '../../Models/CampaignType';
import { RepositoryFactoryContext } from '../../index';
import './CampaignsSisps.scss';

interface ScheduleUpdate {
    schedule_date: string;
    schedule_time: string;
    send_mode?: string;
    send_window_start?: string;
    send_window_end?: string;
    send_days?: string;
    daily_send_limit?: number;
    auto_schedule?: boolean;
}

interface ScheduleContext {
    can_slow_send: boolean;
    force_slow_send: boolean;
    send_mode: number;
    window: { start: string; end: string } | null;
    busy_until: string | null;
    send_days: string | null;
    daily_send_limit: number;
    auto_schedule: boolean;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const maskToDays = (mask: string | null): boolean[] =>
    mask && mask.length === 7 ? mask.split('').map((c) => c === '1') : [true, true, true, true, true, true, true];

const daysToMask = (days: boolean[]): string => days.map((day) => (day ? '1' : '0')).join('');

// Applies an "HH:mm" value from a native time input onto a date, keeping the date portion.
const timeOnDate = (base: Date, value: string): Date => {
    const [hours, minutes] = value.split(':').map((part) => parseInt(part, 10));
    return setMinutes(setHours(base, hours || 0), minutes || 0);
};

interface Props {
    uniqueKey: string;
    onSuccess: (event: any) => Promise<boolean>;
}

// Mirrors the send wizard's pretty-checkbox markup so the dedicated, auto-track and send-day
// toggles match the form that created the schedule rather than rendering as bare browser boxes.
const SispCheckbox: FunctionComponent<{
    id: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
}> = ({ id, label, checked, disabled, onChange }) => (
    <div className="pretty p-default p-curve p-fill p-icon p-smooth p-bigger-medium">
        <input type="checkbox" id={id} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <div className="state p-info">
            <label className="checkbox ch-medium" htmlFor={id}>
                {label}
            </label>
            <i className="icon far fa-check" data-fa-transform="shrink-8" />
        </div>
    </div>
);

const CampaignsEditSisp: FunctionComponent<Props> = (props: Props) => {
    const campaignsRepository = useContext(RepositoryFactoryContext).getApiRepository(new CampaignsRequest());
    const permissions = useContext(UserPermissionsContext);
    const customTagsEnabled = permissions.campaignsCustomTags.isEnabled;

    // State: General
    const [shown, setShown] = useState<boolean>(false);
    const [scheduleValid, setScheduleValid] = useState<boolean>(true);
    const [rowId, setRowId] = useState<number>(0);

    // State: Datepicker
    const [currentDate, setCurrentDate] = useState<Date>();
    const [scheduledDate, setScheduledDate] = useState<Date>();

    // State: Tags
    const [tags, setTags] = useState<{ value: number; label: any }[]>();

    // State: Dedicated-owl send window + overlap guard
    const [canSlowSend, setCanSlowSend] = useState<boolean>(false);
    // slowSend tracks whether the campaign uses the dedicated parliament (dedicated or both);
    // sendMode preserves the exact routing so the locked-in send method is posted back unchanged.
    const [slowSend, setSlowSend] = useState<boolean>(false);
    const [sendMode, setSendMode] = useState<number>(0);
    const [busyUntil, setBusyUntil] = useState<Date | undefined>();
    const [windowStart, setWindowStart] = useState<Date | undefined>();
    const [windowEnd, setWindowEnd] = useState<Date | undefined>();
    const [sendDays, setSendDays] = useState<boolean[]>(maskToDays(null));
    const [dailySendLimit, setDailySendLimit] = useState<number>(0);
    const [autoSchedule, setAutoSchedule] = useState<boolean>(false);

    const parseTime = (time: string): Date => parse(time, 'HH:mm:ss', new Date());

    useEffect(() => {
        setScheduleValid(true);
        EventBusInstance.subscribe('show-hoverover-component', (event: AppEvent<CampaignType>) => {
            if (event.target !== props.uniqueKey) return;
            setRowId(event.message.id!);
            const currentScheduledDate: Date = parse(event.message.scheduled, 'do, LLL yyyy h:mma', new Date());
            setScheduledDate(currentScheduledDate);
            setCurrentDate(new Date());

            // Pull the slow-send scheduling context: whether the client may slow-send, the
            // campaign's current send method/window/days, and the earliest its mail servers are
            // free (to refuse a date that overlaps an in-flight campaign).
            setCanSlowSend(false);
            setSlowSend(false);
            setBusyUntil(undefined);
            setWindowStart(undefined);
            setWindowEnd(undefined);
            setSendDays(maskToDays(null));
            setDailySendLimit(0);
            setAutoSchedule(false);
            campaignsRepository
                .post_action('schedule_context', event.message.id!)
                .then((result: any) => {
                    const context = result.data as ScheduleContext;
                    setCanSlowSend(context.can_slow_send);
                    const usesDedicated = context.send_mode === 1 || context.send_mode === 2;
                    setSendMode(context.force_slow_send ? 1 : context.send_mode);
                    setSlowSend(usesDedicated || context.force_slow_send);
                    setBusyUntil(
                        context.busy_until
                            ? startOfMinute(parse(context.busy_until, 'yyyy-MM-dd HH:mm:ss', new Date()))
                            : undefined,
                    );
                    setWindowStart(context.window ? parseTime(context.window.start) : undefined);
                    setWindowEnd(context.window ? parseTime(context.window.end) : undefined);
                    setSendDays(maskToDays(context.send_days));
                    setDailySendLimit(context.daily_send_limit ?? 0);
                    setAutoSchedule(context.auto_schedule ?? false);
                })
                .catch(() => {
                    // Leave the SISP as the plain date/time editor if the context can't load.
                });

            if (customTagsEnabled) {
                setTags(
                    event.message.tags?.map((t) => {
                        return {
                            value: t.id ?? 0,
                            label: <ClientTagPill tag={t} />,
                        };
                    }) ?? [],
                );
            }

            setShown(true);
        });
    }, [shown]);

    const isWindowValid = (): boolean => {
        if (!windowStart && !windowEnd) return true;
        if (!windowStart || !windowEnd) return false;
        return windowEnd > windowStart;
    };

    const handleUpdate = async (): Promise<boolean> => {
        const scheduleUpdate: ScheduleUpdate = {
            schedule_date: format(scheduledDate!, 'dd/MM/yyyy'),
            schedule_time: format(scheduledDate!, 'HH:mm'),
        };

        // Post the slow-send choice (and, when on, its window/days) only when the client can
        // slow-send, so a normal client's edit is unaffected and the backend leaves the
        // window/days untouched when not sent.
        if (canSlowSend) {
            scheduleUpdate.send_mode = String(slowSend ? sendMode || 1 : 0);
            if (slowSend) {
                scheduleUpdate.send_window_start = windowStart ? format(windowStart, 'HH:mm') : '';
                scheduleUpdate.send_window_end = windowEnd ? format(windowEnd, 'HH:mm') : '';
                scheduleUpdate.send_days = daysToMask(sendDays);
                scheduleUpdate.daily_send_limit = Math.max(0, dailySendLimit || 0);
                scheduleUpdate.auto_schedule = autoSchedule;
            }
        }

        const scheduleUpdateSuccess = await campaignsRepository
            .post_action('update_schedule', rowId, scheduleUpdate)
            .then((results: any) => {
                props.onSuccess(results.data);
                showBanner({
                    message: 'Campaign scheduling has been successfully saved.',
                    level: LogLevel.SUCCESS,
                });
                return Promise.resolve(true);
            })
            .catch((err: any) => {
                showBanner({
                    message: err?.message ?? err,
                    level: LogLevel.ERROR,
                });
                return Promise.resolve(false);
            });

        let tagUpdateSuccess = true;
        if (customTagsEnabled && tags !== undefined) {
            tagUpdateSuccess = await campaignsRepository
                .post_action(
                    'update_tags',
                    rowId,
                    tags.map((v) => v.value),
                )
                .then((results: any) => {
                    props.onSuccess(results.data);
                    showBanner({
                        message: 'Campaign tags saved successfully.',
                        level: LogLevel.SUCCESS,
                    });
                    return Promise.resolve(true);
                })
                .catch((err: any) => {
                    showBanner({
                        message: err?.message ?? err,
                        level: LogLevel.ERROR,
                    });
                    return Promise.resolve(false);
                });
        }

        return Promise.resolve(scheduleUpdateSuccess && tagUpdateSuccess);
    };

    const validate = async (): Promise<boolean> => {
        // Auto-track starts when the dedicated servers are free, not at the chosen time, so a past
        // time is irrelevant; the backend applies the full schedule checks either way.
        let valid: boolean = autoSchedule || !isPast(scheduledDate!);
        if (slowSend && (!isWindowValid() || !sendDays.some(Boolean))) {
            valid = false;
        }
        if (!valid) setCurrentDate(new Date());
        setScheduleValid(valid);
        return valid;
    };

    const addAndRoundMinutes = (date: Date, addAmount: number, roundAmount: number): Date => {
        let newDate: Date = addMinutes(date, addAmount);
        return roundToNearestMinutes(newDate, { nearestTo: roundAmount });
    };

    const handleDateUpdate = (date: Date) => {
        // If scheduled date is updated to the current day and the current
        // scheduled time is in the past then set the scheduled time to a
        // time in the future
        if (isSameDay(date, currentDate!) && isPast(date)) {
            const timeUpdate: Date = addAndRoundMinutes(currentDate!, 5, 5);
            date = setHours(date, getHours(timeUpdate));
            date = setMinutes(date, getMinutes(timeUpdate));
        }
        setScheduledDate(date);
    };

    const onSubmitForm = async (e: FormEvent) => {
        e.preventDefault();
        if ((await validate()) && (await handleUpdate())) setShown(false);
    };

    return (
        <Sisp
            isOpen={shown}
            onSubmit={handleUpdate}
            onCancel={() => {
                setShown(false);
            }}
            validate={validate}
        >
            <h4>Edit Campaign</h4>
            <Form onSubmit={onSubmitForm}>
                {slowSend && (
                    <Form.Group className="campaign-slow-send">
                        <Form.Label>How to Send</Form.Label>
                        <select className="campaigns-date-picker" value={sendMode} onChange={() => undefined} disabled>
                            <option value={0}>Shared</option>
                            <option value={1}>Dedicated</option>
                            <option value={2}>Both</option>
                        </select>
                    </Form.Group>
                )}
                <div className="campaign-schedule-row">
                    <Form.Group className="campaign-date">
                        <Form.Label>Send On This Date</Form.Label>
                        <DatePicker
                            className="campaigns-date-picker"
                            selected={scheduledDate}
                            onChange={handleDateUpdate}
                            dateFormat="dd/MM/yyyy"
                            minDate={currentDate}
                            disabled={autoSchedule}
                        />
                    </Form.Group>
                    <Form.Group className="campaign-time">
                        <Form.Label>
                            At This Time
                            {autoSchedule && (
                                <HelpTooltip
                                    title="Estimated start"
                                    helptext="Your campaign begins as soon as your dedicated mail servers are free."
                                    trigger={['hover', 'focus']}
                                />
                            )}
                        </Form.Label>
                        <input
                            type="time"
                            className="campaigns-date-picker"
                            value={scheduledDate ? format(scheduledDate, 'HH:mm') : ''}
                            disabled={autoSchedule}
                            onChange={(e) => {
                                if (e.target.value) setScheduledDate(timeOnDate(scheduledDate!, e.target.value));
                            }}
                        />
                    </Form.Group>
                </div>
                {slowSend && (
                    <Form.Group className="campaign-auto-schedule">
                        <SispCheckbox
                            id="campaign_auto_schedule"
                            label="Start as soon as my mail servers are free"
                            checked={autoSchedule}
                            onChange={(checked) => {
                                setAutoSchedule(checked);
                                if (checked && busyUntil) {
                                    setScheduledDate(busyUntil);
                                }
                            }}
                        />
                    </Form.Group>
                )}
                {slowSend && (
                    <Form.Group className="campaign-send-window">
                        <Form.Label>
                            Send Window <small className="text-muted">(optional)</small>
                        </Form.Label>
                        <div className="send-window-row">
                            <input
                                type="time"
                                className="campaigns-date-picker"
                                aria-label="Send window start"
                                value={windowStart ? format(windowStart, 'HH:mm') : ''}
                                onChange={(e) =>
                                    setWindowStart(e.target.value ? timeOnDate(new Date(), e.target.value) : undefined)
                                }
                            />
                            <input
                                type="time"
                                className="campaigns-date-picker"
                                aria-label="Send window end"
                                value={windowEnd ? format(windowEnd, 'HH:mm') : ''}
                                onChange={(e) =>
                                    setWindowEnd(e.target.value ? timeOnDate(new Date(), e.target.value) : undefined)
                                }
                            />
                        </div>
                        <Form.Text className="text-muted">
                            Your mail servers only send during these hours; leave blank to send at any time.
                        </Form.Text>
                        {busyUntil && (
                            <Form.Text className="text-muted">
                                Suggested start: your mail servers are free from{' '}
                                {format(busyUntil, 'dd/MM/yyyy HH:mm')}. You can pick any time; a campaign that would
                                overlap an earlier one waits until the servers are free.
                            </Form.Text>
                        )}
                    </Form.Group>
                )}
                {slowSend && (
                    <Form.Group className="campaign-send-days">
                        <Form.Label>Send Days</Form.Label>
                        <div>
                            {DAY_LABELS.map((label, index) => (
                                <SispCheckbox
                                    key={label}
                                    id={`send_day_${index}`}
                                    label={label}
                                    checked={sendDays[index]}
                                    onChange={(checked) => {
                                        const next = [...sendDays];
                                        next[index] = checked;
                                        setSendDays(next);
                                    }}
                                />
                            ))}
                        </div>
                        <Form.Text className="text-muted">
                            The campaign only sends on the ticked days; on other days it pauses until the next.
                        </Form.Text>
                    </Form.Group>
                )}
                {slowSend && (
                    <Form.Group className="campaign-daily-limit">
                        <Form.Label>
                            Send no more than <small className="text-muted">(emails per day)</small>
                        </Form.Label>
                        <Form.Control
                            type="number"
                            min={0}
                            step={1}
                            value={dailySendLimit || ''}
                            onChange={(e) => setDailySendLimit(Math.max(0, parseInt(e.target.value, 10) || 0))}
                            placeholder="0"
                        />
                        <Form.Text className="text-muted">
                            Leave at 0 to send as fast as your mail servers allow; set a lower number to send more
                            slowly.
                        </Form.Text>
                    </Form.Group>
                )}
                <Form.Group>
                    {!scheduleValid && slowSend && !sendDays.some(Boolean) && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            Please select at least one sending day.
                        </Form.Control.Feedback>
                    )}
                    {!scheduleValid && slowSend && !isWindowValid() && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            The send window end time must be after the start time.
                        </Form.Control.Feedback>
                    )}
                    {!scheduleValid && isWindowValid() && (!slowSend || sendDays.some(Boolean)) && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            Sorry, please select a schedule in the future.
                        </Form.Control.Feedback>
                    )}
                </Form.Group>
                {customTagsEnabled && (
                    <TagsEditDropdown
                        onChange={(tags) => {
                            setTags(tags);
                        }}
                        existingTags={tags}
                    />
                )}
            </Form>
        </Sisp>
    );
};

export default CampaignsEditSisp;
